import React from 'react';
import { Link } from 'react-router-dom';
import SEO from '../../components/SEO/SEO';
import { getSEOContent } from '../../data/seoContent';

const CATEGORY_LINKS = [
  {
    path: '/mokomieji-zaidimai',
    name: 'Mokomieji žaidimai',
    shortName: 'Visi mokomieji žaidimai',
  },
  {
    path: '/matematikos-zaidimai',
    name: 'Matematikos žaidimai',
    shortName: 'Matematika',
  },
  {
    path: '/skaitymo-zaidimai',
    name: 'Skaitymo žaidimai',
    shortName: 'Skaitymas',
  },
  {
    path: '/lietuviu-kalbos-zaidimai',
    name: 'Lietuvių kalbos žaidimai',
    shortName: 'Lietuvių kalba',
  },
  {
    path: '/logopediniai-zaidimai',
    name: 'Logopediniai ir kalbos lavinimo žaidimai',
    shortName: 'Kalbos lavinimas',
  },
];

const DEFAULT_FEATURES = [
  {
    icon: '🤖',
    title: 'AI pritaikytos užduotys',
    text:
      'SmartPanda AI gali generuoti naujas užduotis, todėl mokymasis gali būti įvairesnis ir neapsiriboti vienu fiksuotu pavyzdžių rinkiniu.',
  },
  {
    icon: '🎮',
    title: 'Mokymasis žaidžiant',
    text:
      'Mokymosi temos pateikiamos kaip interaktyvūs žaidimai, kuriuose vaikas gali aktyviai spręsti užduotis ir gauti grįžtamąjį ryšį.',
  },
  {
    icon: '📈',
    title: '3 sudėtingumo lygiai',
    text:
      'Skirtingi sudėtingumo lygiai leidžia pasirinkti vaikui tinkamesnį iššūkį ir palaipsniui pereiti prie sudėtingesnių užduočių.',
  },
];

const DEFAULT_FAQ = [
  {
    question: 'Kokio amžiaus vaikams skirti SmartPanda žaidimai?',
    answer:
      'SmartPanda mokomieji žaidimai skirti vaikams nuo 4 iki 14 metų. Konkretus žaidimas ir jo sudėtingumas priklauso nuo mokymosi temos.',
  },
  {
    question: 'Ar žaidimai turi skirtingus sudėtingumo lygius?',
    answer:
      'Taip. SmartPanda žaidimuose naudojami trys sudėtingumo lygiai: lengvas, vidutinis ir sunkus.',
  },
  {
    question: 'Ar SmartPanda naudoja dirbtinį intelektą?',
    answer:
      'Taip. SmartPanda naudoja AI naujoms mokymosi užduotims generuoti ir mokymosi turiniui paįvairinti.',
  },
  {
    question: 'Ar galima SmartPanda išbandyti nemokamai?',
    answer:
      'Taip. SmartPanda turi nemokamą planą, todėl platformą galima išbandyti prieš pasirenkant mokamą planą.',
  },
];

const GameCard = ({ game }) => (
  <article className="sp-seo-game-card">
    <Link
      to={`/zaidimai/${game.slug}`}
      className="sp-seo-game-card-link"
      aria-label={`Sužinoti daugiau apie žaidimą „${game.name}“`}
    >
      <div className="sp-seo-game-icon" aria-hidden="true">
        {game.icon}
      </div>

      <div>
        <h3>{game.name}</h3>
        <p>{game.skill}</p>
      </div>
    </Link>
  </article>
);

const SEOLandingPage = ({
  title,
  description,
  canonical,
  eyebrow,
  h1,
  intro,
  games = [],
  features = DEFAULT_FEATURES,
  faq = DEFAULT_FAQ,
  accent = '#58CC02',
  categoryDescription,
  contentSections = [],
  learningOutcomes = [],
  about = [],
  ctaText = 'Išbandyti SmartPanda',
  category = 'educational',
  pageType = 'category',
  breadcrumbName,
  childLinks = [],
  relatedLinks = [],
  trustAgeLabel = '4–14 metų',
}) => {
  const currentCategory =
    CATEGORY_LINKS.find((category) => category.path === canonical) ||
    null;

  const relatedCategories = CATEGORY_LINKS.filter(
    (category) => category.path !== canonical
  );

  const seoContent = getSEOContent(
    pageType === 'category' ? category : 'educational'
  );

  const resolvedBreadcrumbName =
    breadcrumbName ||
    currentCategory?.name ||
    h1;

  const breadcrumbs = [
    {
      name: 'Pradžia',
      path: '/',
    },
    {
      name: resolvedBreadcrumbName,
      path: canonical,
    },
  ];

  return (
    <>
      <SEO
        title={title}
        description={description}
        canonical={canonical}
        includeWebsiteSchema={false}
        pageType={
          pageType === 'topic' || pageType === 'age'
            ? 'CollectionPage'
            : 'WebPage'
        }
        breadcrumbs={breadcrumbs}
        faq={faq}
        games={games}
        about={about}
        learningOutcomes={learningOutcomes}
      />

      <main className="sp-seo-page">
        <nav
          className="sp-seo-breadcrumbs"
          aria-label="Navigacija"
        >
          <div className="sp-seo-container">
            <a href="/">Pradžia</a>
            <span aria-hidden="true">→</span>
            <span>{resolvedBreadcrumbName}</span>
          </div>
        </nav>

        <section
          className="sp-seo-hero"
          style={{ '--sp-accent': accent }}
        >
          <div className="sp-seo-container">
            <div className="sp-seo-eyebrow">{eyebrow}</div>

            <h1>{h1}</h1>

            <p className="sp-seo-intro">{intro}</p>

            <div className="sp-seo-hero-actions">
              <a
                href="/register"
                className="sp-seo-primary-btn"
              >
                {ctaText} →
              </a>

              <a
                href="#zaidimai"
                className="sp-seo-secondary-btn"
              >
                Peržiūrėti žaidimus
              </a>
            </div>

            <div className="sp-seo-trust-row">
              <span>🎮 {games.length} žaidimai šioje kategorijoje</span>
              <span>🤖 AI generuojamos užduotys</span>
              <span>📈 3 lygiai</span>
              <span>👧🧒 {trustAgeLabel}</span>
            </div>
          </div>
        </section>

        <section className="sp-seo-section">
          <div className="sp-seo-container">
            <div className="sp-seo-section-heading">
              <span>✨</span>
              <h2>Kodėl verta mokytis su SmartPanda?</h2>
            </div>

            <div className="sp-seo-feature-grid">
              {features.map((feature) => (
                <article
                  className="sp-seo-feature-card"
                  key={feature.title}
                >
                  <div className="sp-seo-feature-icon">
                    {feature.icon}
                  </div>

                  <h3>{feature.title}</h3>

                  <p>{feature.text}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        {learningOutcomes.length > 0 && (
          <section className="sp-seo-section sp-seo-learning-outcomes">
            <div className="sp-seo-container">
              <div className="sp-seo-section-heading">
                <span>🎯</span>
                <h2>Ką vaikas gali lavinti?</h2>
              </div>

              <div className="sp-seo-outcomes-grid">
                {learningOutcomes.map((outcome) => (
                  <div
                    className="sp-seo-outcome"
                    key={outcome}
                  >
                    <span aria-hidden="true">✓</span>
                    <span>{outcome}</span>
                  </div>
                ))}
              </div>
            </div>
          </section>
        )}

        {seoContent.topics.length > 0 && (
          <section className="sp-seo-section sp-seo-topics-section">
            <div className="sp-seo-container">
              <div className="sp-seo-section-heading">
                <span>📚</span>
                <h2>{seoContent.topicsTitle}</h2>
              </div>

              <p className="sp-seo-category-description">
                {seoContent.topicsIntro}
              </p>

              <div className="sp-seo-topics-grid">
                {seoContent.topics.map((topic) => (
                  <article
                    className="sp-seo-topic-card"
                    key={topic.title}
                  >
                    <h3>{topic.title}</h3>
                    <p>{topic.description}</p>

                    {topic.path && (
                      <a href={topic.path}>
                        Peržiūrėti žaidimus →
                      </a>
                    )}
                  </article>
                ))}
              </div>
            </div>
          </section>
        )}

        <section
          className="sp-seo-section sp-seo-games-section"
          id="zaidimai"
        >
          <div className="sp-seo-container">
            <div className="sp-seo-section-heading">
              <span>🎮</span>
              <h2>Žaidimai ir užduotys</h2>
            </div>

            {categoryDescription && (
              <p className="sp-seo-category-description">
                {categoryDescription}
              </p>
            )}

            <div className="sp-seo-games-grid">
              {games.map((game) => (
                <GameCard
                  game={game}
                  key={game.id || game.name}
                />
              ))}
            </div>
          </div>
        </section>

        {contentSections.length > 0 && (
          <section className="sp-seo-section sp-seo-content-section">
            <div className="sp-seo-container">
              {contentSections.map((section) => (
                <article
                  className="sp-seo-content-block"
                  key={section.title}
                >
                  <h2>{section.title}</h2>

                  {(Array.isArray(section.paragraphs)
                    ? section.paragraphs
                    : typeof section.text === 'string'
                      ? section.text.split('\n').filter(Boolean)
                      : []
                  ).map((paragraph, index) => (
                    <p key={index}>{paragraph}</p>
                  ))}
                </article>
              ))}
            </div>
          </section>
        )}

        <section className="sp-seo-section">
          <div className="sp-seo-container">
            <div className="sp-seo-learning-box">
              <div className="sp-seo-learning-icon">🤖</div>

              <div>
                <h2>AI suteikia daugiau mokymosi įvairovės</h2>

                <p>
                  SmartPanda sujungia interaktyvius mokomuosius žaidimus
                  su dirbtiniu intelektu. AI gali generuoti naujas
                  užduotis, todėl vaikas gali daugiau praktikuotis su ta
                  pačia mokymosi tema, bet susidurti su naujais
                  klausimais ir pavyzdžiais.
                </p>
              </div>
            </div>
          </div>
        </section>

        {pageType === 'category' && childLinks.length > 0 && (
          <section className="sp-seo-section">
            <div className="sp-seo-container">
              <div className="sp-seo-section-heading">
                <span>📚</span>
                <h2>Šios kategorijos temos</h2>
              </div>

              <div className="sp-seo-related-grid">
                {childLinks.map((link) => (
                  <a
                    key={link.path}
                    href={link.path}
                    className="sp-seo-related-card"
                  >
                    <strong>{link.name}</strong>
                    <span>Peržiūrėti →</span>
                  </a>
                ))}
              </div>
            </div>
          </section>
        )}

        <section className="sp-seo-section">
          <div className="sp-seo-container">
            <div className="sp-seo-section-heading">
              <span>🧭</span>
              <h2>Kitos mokymosi kategorijos</h2>
            </div>

            <p className="sp-seo-category-description">
              SmartPanda turi skirtingų mokymosi sričių žaidimų.
              Pasirinkite kitą kategoriją ir atraskite daugiau
              interaktyvių užduočių.
            </p>

            <div className="sp-seo-related-grid">
              {(relatedLinks.length > 0
                ? relatedLinks
                : relatedCategories
              ).map((link) => (
                <a
                  key={link.path}
                  href={link.path}
                  className="sp-seo-related-card"
                >
                  <strong>{link.name}</strong>
                  <span>Peržiūrėti →</span>
                </a>
              ))}
            </div>
          </div>
        </section>

        <section className="sp-seo-section">
          <div className="sp-seo-container sp-seo-faq-container">
            <div className="sp-seo-section-heading">
              <span>❓</span>
              <h2>Dažniausiai užduodami klausimai</h2>
            </div>

            <div className="sp-seo-faq-list">
              {faq.map((item) => (
                <details
                  key={item.question}
                  className="sp-seo-faq"
                >
                  <summary>{item.question}</summary>
                  <p>{item.answer}</p>
                </details>
              ))}
            </div>
          </div>
        </section>

        <section
          className="sp-seo-final-cta"
          style={{ '--sp-accent': accent }}
        >
          <div className="sp-seo-container">
            <h2>Leiskite vaikui mokytis žaidžiant</h2>

            <p>
              Išbandykite interaktyvius SmartPanda mokomuosius žaidimus
              ir atraskite kitokį būdą praktikuoti svarbius mokymosi
              įgūdžius.
            </p>

            <a
              href="/register"
              className="sp-seo-primary-btn"
            >
              Pradėti nemokamai →
            </a>
          </div>
        </section>
      </main>
    </>
  );
};

export default SEOLandingPage;
